import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_THRESHOLD = 72;
const MAX_PULL = 96;

function scrollTopNow(): number {
  const el = document.scrollingElement ?? document.documentElement;
  return el.scrollTop ?? 0;
}

/**
 * Pull-to-refresh: con la pagina già in cima, trascina verso il bassio (gesto tipico mobile)
 * per aggiornare i dati.
 */
export function usePullToRefresh(
  onRefresh: () => void | Promise<void>,
  options: { enabled?: boolean; threshold?: number } = {}
) {
  const { enabled = true, threshold = DEFAULT_THRESHOLD } = options;
  const [pullPx, setPullPx] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const pullPxRef = useRef(0);
  const startYRef = useRef<number | null>(null);
  const armedRef = useRef(false);
  const inFlightRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);

  onRefreshRef.current = onRefresh;

  const atTop = useCallback(() => scrollTopNow() < 2, []);

  useEffect(() => {
    if (!enabled) {
      setPullPx(0);
      pullPxRef.current = 0;
      return;
    }

    const onTouchStart = (e: TouchEvent) => {
      if (inFlightRef.current) return;
      if (!atTop()) return;
      startYRef.current = e.touches[0].clientY;
      armedRef.current = true;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!armedRef.current || startYRef.current === null || inFlightRef.current) return;
      if (!atTop()) {
        armedRef.current = false;
        pullPxRef.current = 0;
        setPullPx(0);
        startYRef.current = null;
        return;
      }
      const dy = e.touches[0].clientY - startYRef.current;
      if (dy <= 0) {
        pullPxRef.current = 0;
        setPullPx(0);
        return;
      }
      const damped = Math.min(dy * 0.4, MAX_PULL);
      pullPxRef.current = damped;
      setPullPx(damped);
      if (damped > 14) {
        e.preventDefault();
      }
    };

    const finishGesture = () => {
      if (!armedRef.current) return;
      armedRef.current = false;
      startYRef.current = null;
      const p = pullPxRef.current;
      pullPxRef.current = 0;
      setPullPx(0);
      if (p >= threshold && !inFlightRef.current) {
        inFlightRef.current = true;
        setRefreshing(true);
        void Promise.resolve(onRefreshRef.current())
          .catch(() => {})
          .finally(() => {
            inFlightRef.current = false;
            setRefreshing(false);
          });
      }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', finishGesture, { passive: true });
    window.addEventListener('touchcancel', finishGesture, { passive: true });

    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', finishGesture);
      window.removeEventListener('touchcancel', finishGesture);
    };
  }, [enabled, atTop, threshold]);

  const progress = Math.min(1, pullPx / threshold);

  return { pullPx, progress, refreshing };
}
